import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface EmailLog {
  id: string;
  reservation_id: string;
  customer_email: string;
  customer_name: string;
  email_type: 'confirmation' | 'modification' | 'cancellation' | 'reminder' | 'followup';
  subject: string;
  sent_at: string;
  resend_email_id: string | null;
  status: 'sent' | 'failed';
  error_message: string | null;
  reservation_date: string;
  reservation_time: string;
  sent_by_user_id: string | null;
}

interface EmailLogsParams {
  page?: number;
  limit?: number;
  emailType?: string;
  status?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
}

interface EmailLogsResponse {
  logs: EmailLog[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const useEmailLogs = (params: EmailLogsParams = {}) => {
  return useQuery({
    queryKey: ['emailLogs', params],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Non authentifié');

      const queryParams = new URLSearchParams();
      if (params.page) queryParams.set('page', params.page.toString());
      if (params.limit) queryParams.set('limit', params.limit.toString());
      if (params.emailType) queryParams.set('emailType', params.emailType);
      if (params.status) queryParams.set('status', params.status);
      if (params.search) queryParams.set('search', params.search);
      if (params.startDate) queryParams.set('startDate', params.startDate);
      if (params.endDate) queryParams.set('endDate', params.endDate);

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-email-logs?${queryParams.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Erreur lors de la récupération des logs');
      }

      return await response.json() as EmailLogsResponse;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });
};
